import { Tag, Typography } from 'antd';

import { formatUsd } from '../shared/billing-format';
import { discountLabel, discountSummary, isDiscounted, type BillingDiscount } from '../shared/discount';

import type { CSSProperties, ReactNode } from 'react';

type Props = {
  /** The list price. */
  cents: number | null | undefined;
  /** The price after the organization's discounts; equal to `cents` when there are none. */
  discountedCents: number | null | undefined;
  /** Text after the amount, e.g. " / month". */
  suffix?: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** The struck-through list price's style (defaults to a lighter, smaller text). */
  listStyle?: CSSProperties;
  testId?: string;
};

/**
 * A price with its discount: "~~$399~~ $339.15 / month". With no discount, just the price.
 * The strike-through keeps the list price readable so the discount is visibly a discount.
 */
export const DiscountedPrice = ({ cents, discountedCents, suffix, className, style, listStyle, testId }: Props) => {
  if (!isDiscounted(cents, discountedCents)) {
    return (
      <Typography.Text className={className} style={style} data-testid={testId}>
        {formatUsd(cents ?? null)}
        {suffix}
      </Typography.Text>
    );
  }
  return (
    <Typography.Text className={className} style={style} data-testid={testId}>
      <Typography.Text delete style={{ opacity: 0.6, fontSize: '0.85em', marginRight: 6, color: 'inherit', ...listStyle }}>
        {formatUsd(cents ?? null)}
      </Typography.Text>
      {formatUsd(discountedCents ?? null)}
      {suffix}
    </Typography.Text>
  );
};

type DiscountTagsProps = {
  discounts: BillingDiscount[];
  /** Full summary (name, code, end date) instead of just "15% off". */
  detailed?: boolean;
  style?: CSSProperties;
};

/** One tag per discount, in the order Stripe applies them. Nothing without discounts. */
export const DiscountTags = ({ discounts, detailed = false, style }: DiscountTagsProps) =>
  discounts.length === 0 ? null : (
    <>
      {discounts.map((discount, index) => (
        <Tag
          key={`${discount.scope}-${index}`}
          variant="filled"
          color="green"
          data-testid="billing-discount"
          style={{ borderRadius: 9999, padding: '3px 9px', fontSize: 11, fontWeight: 600, lineHeight: 1, margin: 0, ...style }}
        >
          {detailed ? discountSummary(discount) : discountLabel(discount)}
        </Tag>
      ))}
    </>
  );
